import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, TextField, Button, MenuItem, Select } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import { put, get } from "../../../../utils/api";
import styles from "../../../../styles/gri/general.module.css";
import AddmultipleTables from "./Tables";
import GRISidebar from "../../../../Layout/GRISidebar";

import ReviewBox from "../../../../Components/Common/ReviewBox";
import { currentYear } from "../../../../utils/generateYearRanges";

const ENERGY_UNIT_TO_MJ = {
  MJ: 1,
  GJ: 1000,
  TJ: 1000000,
};

const convertEnergyAmount = (amount, fromUnit, toUnit) => {
  if (
    amount === "" ||
    amount == null ||
    !fromUnit ||
    !toUnit ||
    fromUnit === toUnit
  ) {
    return amount;
  }

  const num = Number(amount);
  if (Number.isNaN(num)) return amount;

  const fromFactor = ENERGY_UNIT_TO_MJ[fromUnit];
  const toFactor = ENERGY_UNIT_TO_MJ[toUnit];
  if (!fromFactor || !toFactor) return amount;

  const converted = (num * fromFactor) / toFactor;
  const rounded = Math.round(converted * 1e10) / 1e10;
  return String(rounded);
};

const convertFormDataAmounts = (formData, fromUnit, toUnit) => {
  const convertField = (amount) => convertEnergyAmount(amount, fromUnit, toUnit);

  return {
    ...formData,
    totalFuelConsumptionNonRenewable: {
      ...formData.totalFuelConsumptionNonRenewable,
      amount: convertField(formData.totalFuelConsumptionNonRenewable.amount),
      unit: toUnit,
    },
    totalRenewableFuelConsumption: {
      ...formData.totalRenewableFuelConsumption,
      amount: convertField(formData.totalRenewableFuelConsumption.amount),
      unit: toUnit,
    },
    energyConsumption: Object.fromEntries(
      Object.entries(formData.energyConsumption).map(([key, item]) => [
        key,
        {
          ...item,
          amount: convertField(item.amount),
          unit: toUnit,
        },
      ]),
    ),
    energySold: Object.fromEntries(
      Object.entries(formData.energySold).map(([key, item]) => [
        key,
        {
          ...item,
          amount: convertField(item.amount),
          unit: toUnit,
        },
      ]),
    ),
    totalEnergyConsumption: {
      ...formData.totalEnergyConsumption,
      unit: toUnit,
    },
  };
};

const GRI302_1 = ({ hide }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [unitsAll, setUnit] = useState("");
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    totalFuelConsumptionNonRenewable: {
      amount: "",
      unit: unitsAll || "",
    },
    totalRenewableFuelConsumption: {
      amount: "",
      unit: unitsAll || "",
    },
    energyConsumption: {
      electricity: {
        amount: "",
        unit: unitsAll || "",
      },
      heat: {
        amount: "",
        unit: unitsAll || "",
      },
      cooling: {
        amount: "",
        unit: unitsAll || "",
      },
      steam: {
        amount: "",
        unit: unitsAll || "",
      },
    },
    energySold: {
      electricity: {
        amount: "",
        unit: "",
      },
      heat: {
        amount: "",
        unit: "",
      },
      cooling: {
        amount: "",
        unit: "",
      },
      steam: {
        amount: "",
        unit: "",
      },
    },
    totalEnergyConsumption: { amount: "", unit: "" },
    standardsMethodologies: "",
    conversionFactors: "",
  });

  const [griReview, setGriReview] = React.useState(() => JSON.parse(localStorage.getItem("griReview")));
  React.useEffect(() => {
    const handleStorage = () => setGriReview(JSON.parse(localStorage.getItem("griReview")));
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);
  React.useEffect(() => {
    setGriReview(JSON.parse(localStorage.getItem("griReview")));
  }, []);

  useEffect(() => {
    // Convert values to numbers (default to 0 if empty)
    const parseNumber = (value) => (isNaN(Number(value)) ? 0 : Number(value));

    // Step 1: Sum of Fuel Consumption
    const fuelConsumption =
      parseNumber(formData.totalFuelConsumptionNonRenewable.amount) +
      parseNumber(formData.totalRenewableFuelConsumption.amount);

    // Step 2: Sum of Energy Consumption
    const energyConsumptionSum = Object.values(
      formData.energyConsumption
    ).reduce((sum, item) => sum + parseNumber(item.amount), 0);

    // Step 3: Sum of Energy Sold
    const energySoldSum = Object.values(formData.energySold).reduce(
      (sum, item) => sum + parseNumber(item.amount),
      0
    );

    // Step 4: Compute Total Energy Consumption
    const totalEnergy = fuelConsumption + energyConsumptionSum - energySoldSum;

    // Step 5: Update `totalEnergyConsumption.amount`
    setFormData((prev) => ({
      ...prev,
      totalEnergyConsumption: {
        ...prev.totalEnergyConsumption,
        amount: totalEnergy.toString(), // Convert back to string if needed
      },
    }));
  }, [
    formData.totalFuelConsumptionNonRenewable.amount,
    formData.totalRenewableFuelConsumption.amount,
    formData.energyConsumption,
    formData.energySold,
  ]);

  const fetchData = async () => {
    await get(`/gri/get?type=GRI300&Id=${id}`)
      .then((res) => {
        if (res?.data?.data?.year ? res?.data?.data?.year < currentYear -1 : false)
          setDisabled(true);
        const response = res.data.data.GRI300.energyConsumption;
        setUnit(response?.totalFuelConsumptionNonRenewable?.unit);
        setFormData({
          totalFuelConsumptionNonRenewable: {
            amount: response?.totalFuelConsumptionNonRenewable?.amount,
            unit: response?.totalFuelConsumptionNonRenewable?.unit,
          },
          totalRenewableFuelConsumption: {
            amount: response?.totalRenewableFuelConsumption?.amount,
            unit: response?.totalRenewableFuelConsumption?.unit,
          },
          energyConsumption: {
            electricity: {
              amount: response?.energyConsumption?.electricity?.amount,
              unit: unitsAll || response?.energyConsumption?.electricity?.unit,
            },
            heat: {
              amount: response?.energyConsumption?.heat?.amount,
              unit: unitsAll || response?.energyConsumption?.heat?.unit,
            },
            cooling: {
              amount: response?.energyConsumption?.cooling?.amount,
              unit: unitsAll || response?.energyConsumption?.cooling?.unit,
            },
            steam: {
              amount: response?.energyConsumption?.steam?.amount,
              unit: unitsAll || response?.energyConsumption?.steam?.unit,
            },
          },
          energySold: {
            electricity: {
              amount: response?.energySold?.electricity?.amount,
              unit: unitsAll || response?.energySold?.electricity?.unit,
            },
            heat: {
              amount: response?.energySold?.heat?.amount,
              unit: unitsAll || response?.energySold?.heat?.unit,
            },
            cooling: {
              amount: response?.energySold?.cooling?.amount,
              unit: unitsAll || response?.energySold?.cooling?.unit,
            },
            steam: {
              amount: response?.energySold?.steam?.amount,
              unit: unitsAll || response?.energySold?.steam?.unit,
            },
          },
          totalEnergyConsumption: {
            amount: response?.totalEnergyConsumption?.amount,
            unit: unitsAll || response?.totalEnergyConsumption?.unit,
          },
          standardsMethodologies: response?.standardsMethodologies,
          conversionFactors: response?.conversionFactors,
        });
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const inputData = [
    {
      defalut: "Equipment Name",
      colwidth: "28%",
      filedName: "energyConsumption",
      fields: [
        {
          title: "Amount",
          field: "amount",
          type:"number"
        },
        {
          title: "Unit",
          field: "unit",
          type: "select",
        },
      ],
    },
    {
      defalut: "Equipment Name",
      colwidth: "28%",
      filedName: "energySold",
      fields: [
        {
          title: "Amount",
          field: "amount",
          type:"number"
        },
        {
          title: "Unit",
          field: "unit",
          type: "select",
        },
      ],
    },
  ];

  const defaultSegments = [
    {
      romanIndex: "i",
      title: "Total electricity sold",
      type: "number",
      placeholder:"Enter Number",
      fieldName: "electricity",
      list: [unitsAll] || ["MJ", "GJ", "TJ", "KWH", "MWH", "GWH"],
    },
    {
      romanIndex: "ii",
      title: "Total heating sold",
      type: "number",
      placeholder:"Enter Number",
      fieldName: "heat",
      list: [unitsAll] || ["MJ", "GJ", "TJ"],
    },
    {
      romanIndex: "iii",
      title: "Total cooling sold",
      type: "number",
      placeholder:"Enter Number",
      fieldName: "cooling",
      list: [unitsAll] || ["MJ", "GJ", "TJ"],
    },
    {
      romanIndex: "iv",
      title: "Total steam sold",
      type: "number",
      placeholder:"Enter Number",
      fieldName: "steam",
      list: [unitsAll] || ["MJ", "GJ", "TJ"],
    },
  ];

  const defaultSegments1 = [
    {
      romanIndex: "i",
      title: "Total electricity consumption within the organization",
      type: "number",
      fieldName: "electricity",
      placeholder:"Enter Number",
      list: ["MJ", "GJ", "TJ", "KWH", "MWH", "GWH"],
    },
    {
      romanIndex: "ii",
      title: "Total heating consumption within the organization",
      type: "number",
      placeholder:"Enter Number",
      fieldName: "heat",
      list: ["MJ", "GJ", "TJ"],
    },
    {
      romanIndex: "iii",
      title: "Total cooling consumption within the organization",
      type: "number",
      placeholder:"Enter Number",
      fieldName: "cooling",
      list: ["MJ", "GJ", "TJ"],
    },
    {
      romanIndex: "iv",
      title: "Total steam consumption within the organization",
      type: "number",
      placeholder:"Enter Number",
      fieldName: "steam",
      list: ["MJ", "GJ", "TJ"],
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (!/^\d*\.?\d*$/.test(value)) return;
    const [main, sub] = name.split(".");
    setFormData((prev) => ({
      ...prev,
      ...(sub
        ? { [main]: { ...prev[main], [sub]: value } } // If nested, update sub-field
        : { [main]: value }), // If no sub-field, update main field
    }));
  };
  const handleChangeText = (e) => {
    const { name, value } = e.target;
    const [main, sub] = name.split(".");
    setFormData((prev) => ({
      ...prev,
      ...(sub
        ? { [main]: { ...prev[main], [sub]: value } } // If nested, update sub-field
        : { [main]: value }), // If no sub-field, update main field
    }));
  };

  const handleChangeSelect = (e) => {
    const { name, value } = e.target;
    const [main, sub] = name.split(".");

    if (
      name === "totalFuelConsumptionNonRenewable.unit" &&
      unitsAll &&
      value &&
      unitsAll !== value
    ) {
      setUnit(value);
      setFormData((prev) => convertFormDataAmounts(prev, unitsAll, value));
      return;
    }

    setUnit(value);
    setFormData((prev) => ({
      ...prev,
      ...(sub
        ? { [main]: { ...prev[main], [sub]: value } } // If nested, update sub-field
        : { [main]: value }), // If no sub-field, update main field
    }));
  };

  const handleSubmit = async () => {
    if (!disabled) {
      const dataToSend = {
        ...formData,
        totalEnergyConsumption: {
          ...formData.totalEnergyConsumption,
          unit: unitsAll,
        },
        totalFuelConsumptionNonRenewable: {
          ...formData.totalFuelConsumptionNonRenewable,
          unit: unitsAll,
        },
        totalRenewableFuelConsumption: {
          ...formData.totalRenewableFuelConsumption,
          unit: unitsAll,
        },
        energyConsumption: Object.fromEntries(
          Object.entries(formData.energyConsumption).map(([key, value]) => [
            key,
            { ...value, unit: unitsAll },
          ])
        ),
        energySold: Object.fromEntries(
          Object.entries(formData.energySold).map(([key, value]) => [
            key,
            { ...value, unit: unitsAll },
          ])
        ),
      };

      try {
        const res = await put(`/gri/update?Id=${id}`, {
          type: "GRI300",
          submitted302_1: true,
          energyConsumption: dataToSend,
        })
          .then((res) => navigate(`/gri/gri300/302-2/${id}`))
          .catch((err) => console.log(err));
      } catch (error) {
        console.log(error);
        // setsnackOpen({
        //   open: true,
        //   message: error.data.message,
        //   severity: "error",
        // });
      }
    } else {
      navigate(`/gri/gri300/302-2/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      {!hide && <p className={styles.section_heading} style={{ textDecoration: "none", marginBottom : hide ? "-1rem" : "0" }}>
        302 : <span className={styles.main_heading_underline}>ENERGY</span>
      </p>}
      <p className={styles.main_heading} style={{marginBottom : hide ? "-1rem" : "0", }}>
        302-1 :{" "}
        <span className={styles.main_heading_underline}>
          Energy consumption within the organization
        </span>
      </p>
      <Box>
        <Box>
          <p className={styles.ques_heading}>
            a. Total fuel consumption within the organization from non-renewable
            sources.
          </p>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mt: 3,
              mb: 2,
              ml: 2,
            }}
          >
            <p>Amount:</p>
            <TextField
              size="small"
              placeholder="Enter Number"
              name={`totalFuelConsumptionNonRenewable.amount`}
              value={formData?.totalFuelConsumptionNonRenewable?.amount}
              onChange={handleChange}
              error={errors.totalRecycled}
              disabled={disabled}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 4,
              mt: 3,
              mb: 2,
              ml: 2,
            }}
          >
            <p>Unit:</p>
            <Select
              sx={{
                height: "2rem",
                width: "24%",
              }}
              name={`totalFuelConsumptionNonRenewable.unit`}
              value={formData?.totalFuelConsumptionNonRenewable?.unit || ""}
              placeholder="Select"
              onChange={handleChangeSelect}
              displayEmpty
              size="small"
              disabled={disabled}
              //  fullWidth
              // disabled={!data?.[DropDownData?.fieldName]?.[list?.fields]?.active}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {["MJ", "GJ", "TJ"].map((data, index) => (
                <MenuItem key={index} value={data}>
                  {data}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
        <Box>
          <p className={styles.ques_heading}>
            b. Total fuel consumption within the organization from renewable
            sources.
          </p>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mt: 3,
              mb: 2,
              ml: 2,
            }}
          >
            <p>Amount:</p>
            <TextField
              size="small"
              placeholder="Enter Number"
              name={`totalRenewableFuelConsumption.amount`}
              value={formData?.totalRenewableFuelConsumption?.amount}
              onChange={handleChange}
              disabled={disabled}
              // type="number"
              // value={formData.totalRecycled}
              // onChange={(e) => setFormData({...formData, totalRecycled: e.target.value })}
              error={errors.totalRecycled}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 4,
              mt: 3,
              mb: 2,
              ml: 2,
            }}
          >
            <p>Unit:</p>
            <Select
              sx={{
                height: "2rem",
                width: "24%",
              }}
              disabled
              name={`totalRenewableFuelConsumption.unit`}
              value={unitsAll || ""}
              placeholder="Select"
              onChange={handleChangeSelect}
              displayEmpty
              size="small"
              //  fullWidth
              // disabled={!data?.[DropDownData?.fieldName]?.[list?.fields]?.active}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {["MJ", "GJ", "TJ"].map((data, index) => (
                <MenuItem key={index} value={data}>
                  {data}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      </Box>
      <AddmultipleTables
        title="c. Provide breakdown of energy consumed"
        inputData={[inputData[0]]}
        defaultSegments={defaultSegments1}
        {...{
          formData,
          setFormData,
          errors,
          setErrors,
          unitsAll,
          disabled1: true,
          disabled: disabled,
          hide: hide
        }}
      />

      <AddmultipleTables
        title="d. Provide breakdown of energy sold"
        inputData={[inputData[1]]}
        {...{
          formData,
          setFormData,
          errors,
          setErrors,
          defaultSegments,
          unitsAll,
          disabled1: true,
          disabled: disabled,
           hide: hide
        }}
      />

      <p
        className={styles.ques_heading}
        style={{
          // marginTop: "2.5rem",
          marginRight: "1rem",
          marginBottom: "0.5rem",
          display: "flex",
        }}
      >
        e. Total energy consumption within the organization :
      </p>
      <Box
        sx={{
          display: "flex",
          // justifyContent:"space-between",
          alignItems: "center",
          gap: 2,
          mb: 3,

          marginLeft: "1.2rem",
        }}
      >
        <TextField
          size="small"
          placeholder="Amount"
          name={`totalEnergyConsumption.amount`}
          value={formData?.totalEnergyConsumption?.amount}
          onChange={undefined}
          disabled
          // type="number"
          // value={formData.totalRecycled}
          // onChange={(e) => setFormData({...formData, totalRecycled: e.target.value })}
          error={errors.totalRecycled}
        />
        <TextField
          size="small"
          placeholder="Unit"
          name={`totalEnergyConsumption.unit`}
          value={unitsAll || ""}
          onChange={handleChangeSelect}
          disabled
          // type="number"
          // value={formData.totalRecycled}
          // onChange={(e) => setFormData({...formData, totalRecycled: e.target.value })}
          error={errors.totalRecycled}
        />
      </Box>
      <Box>
        <p
          className={styles.ques_heading}
          style={{
            // marginTop: "2.5rem",
            marginRight: "1rem",
            marginBottom: "0.5rem",
            display: "flex",
          }}
        >
          f. Describe the standard, methodologies, assumptions, and/or
          calculation tools used.
        </p>
        <TextField
          placeholder="Enter Text"
          name="standardsMethodologies"
          value={formData?.standardsMethodologies}
          onChange={handleChangeText}
          sx={{
            marginLeft: "1.2rem",
            width: "97.7%",
          }}
          size="small"
          fullWidth
          disabled={disabled}
        />

        <p
          className={styles.ques_heading}
          style={{
            // marginTop: "2.5rem",
            marginRight: "1rem",
            marginBottom: "0.5rem",
            display: "flex",
          }}
        >
          g. Provide the source of the conversion factors used.
        </p>
        <TextField
          size="small"
          placeholder="Enter Text"
          sx={{
            marginLeft: "1.2rem",
            width: "97.7%",
          }}
          name="conversionFactors"
          value={formData?.conversionFactors}
          onChange={handleChangeText}
          fullWidth
          disabled={disabled}
        />
      </Box>
    </>
  );

  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98%" }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};
export default GRI302_1;
